import { morph } from '@theme/morph';

const PRODUCT_PATH_PATTERN = /\/products\/[^/]+\/?$/;
const DIALOG_CLOSE_EVENT = 'dialog:close';
const MOBILE_BREAKPOINT = '(max-width: 749px)';
const VARIANT_MEDIA_GROUP_CLASS = 'fg-product-preview__media-group';
const VARIANT_GALLERY_UPDATE_EVENT = 'fg:product-preview:gallery-updated';
const SHOPIFY_PRODUCT_SELECT_EVENT = 'shopify:product:select';
const PRODUCT_PREVIEW_BUILD = '1.7.19';
const PREVIEW_QUERY_PARAM = 'fg-product';
const REVIEW_QUERY_PARAM = 'fg-write-review';
const JUDGE_ME_WIDGET_SELECTOR = '.jdgm-review-widget';
const JUDGE_ME_WRITE_SELECTOR = '[data-testid="write-review-button"], .jdgm-write-rev-link';

/**
 * Opens internal product links in Horizon's product dialog and turns the
 * selected variant's product media into a swipeable, arrow-controlled gallery.
 */
class FGProductPreview {
  /** @type {Map<string, Element>} */
  #cache = new Map();

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {number} */
  #requestId = 0;

  /** @type {HTMLElement | null} */
  #trigger = null;

  /** @type {URL | null} */
  #currentProductUrl = null;

  /** @type {boolean} */
  #productNavigationLoading = false;

  /** @type {URL | null} */
  #urlBeforePreview = null;

  /** @type {number} */
  #judgeLoaderLoadId = 0;

  /** @type {number} */
  #variantGalleryRequestId = 0;

  constructor() {
    document.documentElement.dataset.fgProductPreviewBuild = PRODUCT_PREVIEW_BUILD;
    document.addEventListener('click', this.#handleProductLink, { capture: true });
    document.addEventListener('click', this.#handleRetry);
    document.addEventListener('click', this.#handleProductNavigation);
    document.addEventListener('click', this.#handleProductAction);
    document.addEventListener(SHOPIFY_PRODUCT_SELECT_EVENT, this.#handlePreviewVariantSelect, {
      capture: true,
    });
    document.addEventListener(VARIANT_GALLERY_UPDATE_EVENT, this.#handleVariantGalleryUpdate);

    const initializeFromUrl = () => {
      this.#openLinkedProduct();
      this.#openRequestedProductReview();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeFromUrl, { once: true });
    } else {
      queueMicrotask(initializeFromUrl);
    }
  }

  /** @param {MouseEvent} event */
  #handleProductLink = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-product-preview-title]')) {
      event.preventDefault();
      return;
    }

    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const quickAddTrigger = event.target.closest('[data-fg-product-preview-trigger]');
    if (quickAddTrigger instanceof HTMLElement) {
      const quickAdd = quickAddTrigger.closest('quick-add-component');
      const quickAddUrl =
        (typeof quickAdd?.productPageUrl === 'string' && quickAdd.productPageUrl) ||
        quickAddTrigger.dataset.productUrl ||
        '';

      try {
        const productUrl = new URL(quickAddUrl, window.location.href);
        if (productUrl.origin !== window.location.origin || !PRODUCT_PATH_PATTERN.test(productUrl.pathname)) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.open(productUrl, quickAddTrigger).catch((error) => {
          if (error?.name !== 'AbortError') {
            console.warn('[FG product preview] Unable to open quick-add product preview.', error);
          }
        });
        return;
      } catch {
        return;
      }
    }

    const interactiveTarget = event.target.closest(
      'button, input, label, select, textarea, [role="button"]'
    );
    let link = event.target.closest('a[href]');

    if (!link && !interactiveTarget) {
      link = event.target.closest('product-card')?.querySelector('a.product-card__link[href]') || null;
    }

    if (!(link instanceof HTMLAnchorElement) || !this.#shouldOpenInPreview(link)) return;

    const productUrl = new URL(link.href, window.location.href);
    event.preventDefault();

    this.open(productUrl, link).catch((error) => {
      if (error?.name !== 'AbortError') {
        console.warn('[FG product preview] Unable to open product preview.', error);
      }
    });
  };

  /** @param {MouseEvent} event */
  #handleRetry = (event) => {
    if (!(event.target instanceof Element)) return;

    const retryButton = event.target.closest('[data-fg-product-preview-retry]');
    if (!(retryButton instanceof HTMLButtonElement)) return;

    const productUrl = retryButton.dataset.productUrl;
    if (!productUrl) return;

    this.open(new URL(productUrl), this.#trigger, true).catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[FG product preview] Retry failed.', error);
    });
  };

  /** @param {MouseEvent} event */
  #handleProductAction = (event) => {
    if (!(event.target instanceof Element)) return;

    const reviewButton = event.target.closest('[data-fg-write-review]');
    if (reviewButton instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#openJudgeMeReview(reviewButton);
      return;
    }

    const copyButton = event.target.closest('[data-fg-copy-preview-link]');
    if (!(copyButton instanceof HTMLButtonElement)) return;

    event.preventDefault();
    this.#copyPreviewLink(copyButton);
  };

  #openLinkedProduct = () => {
    const pageUrl = new URL(window.location.href);
    const handle = pageUrl.searchParams.get(PREVIEW_QUERY_PARAM)?.trim().toLowerCase() || '';
    if (!/^[a-z0-9][a-z0-9-]*$/.test(handle)) return;

    const returnUrl = new URL(pageUrl.toString());
    returnUrl.searchParams.delete(PREVIEW_QUERY_PARAM);
    this.#urlBeforePreview = returnUrl;

    const storefrontRoot = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    const productUrl = new URL(`${storefrontRoot}products/${handle}`, window.location.origin);
    this.open(productUrl, null).catch((error) => {
      if (error?.name !== 'AbortError') {
        console.warn('[FG product preview] Unable to open linked product preview.', error);
      }
    });
  };

  #openRequestedProductReview = () => {
    const pageUrl = new URL(window.location.href);
    if (
      pageUrl.searchParams.get(REVIEW_QUERY_PARAM) !== '1' ||
      !PRODUCT_PATH_PATTERN.test(pageUrl.pathname)
    ) {
      return;
    }

    let attempts = 0;
    const openTimer = window.setInterval(() => {
      attempts += 1;
      const judgeButton = document.querySelector(
        `${JUDGE_ME_WIDGET_SELECTOR} :is(${JUDGE_ME_WRITE_SELECTOR})`
      );

      if (judgeButton instanceof HTMLElement) {
        window.clearInterval(openTimer);
        judgeButton.click();
        pageUrl.searchParams.delete(REVIEW_QUERY_PARAM);
        window.history.replaceState(window.history.state, '', pageUrl.toString());
        return;
      }

      if (attempts >= 120) window.clearInterval(openTimer);
    }, 100);
  };

  /** @param {MouseEvent} event */
  #handleProductNavigation = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest(
      '[data-fg-product-preview-previous], [data-fg-product-preview-next]'
    );
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    event.preventDefault();
    event.stopPropagation();

    const direction = button.hasAttribute('data-fg-product-preview-next') ? 1 : -1;
    this.#navigateProduct(direction).catch((error) => {
      if (error?.name !== 'AbortError') {
        console.warn('[FG product preview] Product navigation failed.', error);
      }
    });
  };

  /** @param {CustomEvent} event */
  #handleVariantGalleryUpdate = (event) => {
    if (!(event.target instanceof Element)) return;
    const modalContent = event.target.closest('[data-product-grid-content]');
    if (!(modalContent instanceof HTMLElement)) return;

    // media-gallery dispatches this event immediately before replacing itself.
    // Prepare the incoming gallery while it still contains Shopify's newly
    // selected variant, then prepare the mounted copy once more after swap.
    const updatedProductGrid = event.detail?.productGrid;
    if (updatedProductGrid instanceof Element) {
      this.#prepareVariantMedia(updatedProductGrid);
    }

    queueMicrotask(() => {
      window.requestAnimationFrame(() => this.#prepareVariantMedia(modalContent));
    });
  };

  /**
   * Horizon renders only the opening variant's assigned image in a gallery
   * when "hide unselected variant media" is enabled. Inside a product preview,
   * that leaves the next selected colour's mockup missing from the mounted DOM.
   * Use the same successful Shopify variant response to replace the preview
   * gallery with the server-rendered gallery for the newly selected variant.
   *
   * This listener runs in capture phase so it registers its promise callback
   * before the gallery component. The replacement is cloned, leaving Horizon's
   * response document intact for its other standard variant listeners.
   *
   * @param {Event & {promise?: Promise<{detail?: {html?: Document}}>} event
   */
  #handlePreviewVariantSelect = (event) => {
    if (!(event.target instanceof Element)) return;

    const modalContent = event.target.closest('#quick-add-modal-content[data-product-grid-content]');
    const previewDialog = modalContent?.closest('dialog[data-product-preview="true"]');
    const responsePromise = event.promise;

    if (
      !(modalContent instanceof HTMLElement) ||
      !(previewDialog instanceof HTMLDialogElement) ||
      !responsePromise ||
      typeof responsePromise.then !== 'function'
    ) {
      return;
    }

    const requestId = ++this.#variantGalleryRequestId;

    responsePromise
      .then(({ detail }) => {
        if (requestId !== this.#variantGalleryRequestId || !modalContent.isConnected) return;

        const responseDocument = detail?.html;
        if (!responseDocument || typeof responseDocument.querySelector !== 'function') return;

        const updatedProductGrid = responseDocument.querySelector('[data-product-grid-content]');
        if (!(updatedProductGrid instanceof Element)) return;

        this.#prepareVariantMedia(updatedProductGrid);

        const serverGallery = updatedProductGrid.querySelector('media-gallery');
        const mountedGallery = modalContent.querySelector('media-gallery');
        if (!(serverGallery instanceof Element) || !(mountedGallery instanceof Element)) return;

        const currentDescription = modalContent.querySelector('[data-fg-product-description]');
        const updatedDescription = updatedProductGrid.querySelector('[data-fg-product-description]');

        if (currentDescription && updatedDescription) {
          currentDescription.replaceWith(updatedDescription.cloneNode(true));
        } else if (!currentDescription && updatedDescription) {
          modalContent.append(updatedDescription.cloneNode(true));
        } else if (currentDescription && !updatedDescription) {
          currentDescription.remove();
        }

        mountedGallery.replaceWith(serverGallery.cloneNode(true));

        queueMicrotask(() => {
          window.requestAnimationFrame(() => this.#prepareVariantMedia(modalContent));
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[FG product preview] Variant gallery update failed.', error);
        }
      });
  };

  /** @param {HTMLAnchorElement} link */
  #shouldOpenInPreview(link) {
    if (
      link.closest('#quick-add-dialog') ||
      link.hasAttribute('download') ||
      link.dataset.productPreview === 'off' ||
      link.getAttribute('rel')?.split(/\s+/).includes('external')
    ) {
      return false;
    }

    const target = link.getAttribute('target');
    if (target && target.toLowerCase() !== '_self') return false;

    let url;
    try {
      url = new URL(link.href, window.location.href);
    } catch {
      return false;
    }

    return url.origin === window.location.origin && PRODUCT_PATH_PATTERN.test(url.pathname);
  }

  /**
   * @param {URL} productUrl
   * @param {HTMLElement | null} trigger
   * @param {boolean} [forceRefresh]
   */
  async open(productUrl, trigger, forceRefresh = false) {
    const requestId = ++this.#requestId;
    this.#removePreviewJudgeModals();
    this.#trigger = trigger;
    this.#currentProductUrl = new URL(productUrl.toString(), window.location.href);
    this.#syncPreviewUrl(this.#currentProductUrl);
    const sourceRect = this.#getPreviewSourceRect(trigger);

    await customElements.whenDefined('quick-add-dialog');

    const dialogComponent = document.getElementById('quick-add-dialog');
    const dialog = dialogComponent?.querySelector('dialog');
    const modalContent = document.getElementById('quick-add-modal-content');

    if (
      !(dialogComponent instanceof HTMLElement) ||
      !(dialog instanceof HTMLDialogElement) ||
      !(modalContent instanceof HTMLElement) ||
      typeof dialogComponent.showDialog !== 'function'
    ) {
      window.location.assign(productUrl.toString());
      return;
    }

    const productKey = productUrl.toString();
    if (forceRefresh) this.#cache.delete(productKey);

    this.#activeFetch?.abort();
    this.#renderLoading(modalContent);
    dialogComponent.dataset.productPreview = 'true';
    dialog.dataset.productPreview = 'true';
    this.#updateProductNavigation();
    this.#setProductNavigationLoading(true);

    const productRequest = this.#fetchProduct(productKey);
    await this.#closeSourceDialog(trigger, dialogComponent);

    if (requestId !== this.#requestId) return;

    if (!dialog.open) {
      dialog.dataset.fgPreviewMotion = 'pending';
      this.#setPreviewMotion(dialog, sourceRect);
      dialogComponent.showDialog();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (requestId !== this.#requestId || !dialog.open) return;
          dialog.dataset.fgPreviewMotion = 'ready';
        });
      });
    }

    dialogComponent.addEventListener(
      DIALOG_CLOSE_EVENT,
      () => {
        if (requestId !== this.#requestId) return;

        this.#requestId += 1;
        this.#activeFetch?.abort();
        this.#activeFetch = null;
        delete dialogComponent.dataset.productPreview;
        delete dialog.dataset.productPreview;
        delete dialog.dataset.fgPreviewMotion;
        dialog.style.removeProperty('--fg-preview-from-x');
        dialog.style.removeProperty('--fg-preview-from-y');
        dialog.style.removeProperty('--fg-preview-from-scale');
        modalContent.removeAttribute('aria-busy');
        this.#currentProductUrl = null;
        this.#removePreviewJudgeModals();
        this.#restorePreviewUrl();
        this.#setProductNavigationLoading(false);
        this.#updateProductNavigation();

        if (this.#trigger?.isConnected) this.#trigger.focus({ preventScroll: true });
      },
      { once: true }
    );

    try {
      const productGrid = await productRequest;
      if (requestId !== this.#requestId) return;

      this.#prepareProductGrid(productGrid, productUrl);
      this.#prepareVariantMedia(productGrid);

      const payload = productGrid.getAttribute('view-event-payload') || '';
      modalContent.setAttribute('view-event-payload', payload);
      morph(modalContent, productGrid);
      this.#applyProductMetadata(modalContent, productGrid);
      modalContent.removeAttribute('aria-busy');
      this.#finishProductContent(modalContent);
      this.#resetScroll(dialogComponent, modalContent);
      this.#setProductNavigationLoading(false);
      this.#updateProductNavigation();
    } catch (error) {
      if (error?.name === 'AbortError' || requestId !== this.#requestId) return;

      this.#renderError(modalContent, productUrl);
      this.#setProductNavigationLoading(false);
      this.#updateProductNavigation();
      throw error;
    }
  }

  /**
   * Loads the previous or next product from the product cards currently present
   * on the page, while keeping the existing product dialog open.
   * @param {-1 | 1 | number} direction
   */
  async #navigateProduct(direction) {
    if (this.#productNavigationLoading || !this.#currentProductUrl) return;

    const products = this.#getProductSequence(this.#currentProductUrl);
    if (products.length < 2) return;

    const currentKey = this.#getProductKey(this.#currentProductUrl);
    const currentIndex = products.findIndex((product) => product.key === currentKey);
    const safeIndex = currentIndex >= 0 ? currentIndex : 0;
    const nextIndex = (safeIndex + (direction > 0 ? 1 : -1) + products.length) % products.length;
    const nextProduct = products[nextIndex];

    this.#setProductNavigationLoading(true);
    await this.open(nextProduct.url, nextProduct.trigger || this.#trigger);
  }

  /**
   * Returns each product once, in the same order as its card appears in the DOM.
   * @param {URL} currentProductUrl
   * @returns {Array<{url: URL, key: string, trigger: HTMLElement | null}>}
   */
  #getProductSequence(currentProductUrl) {
    /** @type {Array<{url: URL, key: string, trigger: HTMLElement | null}>} */
    const products = [];
    const seen = new Set();
    const sequenceRoot =
      this.#trigger?.closest('[data-testid="product-grid"], .product-grid, [ref="grid"]') || document;

    /**
     * @param {string} rawUrl
     * @param {HTMLElement | null} trigger
     */
    const addProduct = (rawUrl, trigger) => {
      let url;
      try {
        url = new URL(rawUrl, window.location.href);
      } catch {
        return;
      }

      if (url.origin !== window.location.origin || !PRODUCT_PATH_PATTERN.test(url.pathname)) return;

      const key = this.#getProductKey(url);
      if (!key || seen.has(key)) return;

      seen.add(key);
      products.push({ url, key, trigger });
    };

    for (const card of sequenceRoot.querySelectorAll('product-card')) {
      if (!(card instanceof HTMLElement) || card.closest('#quick-add-dialog')) continue;
      const trigger = card.querySelector('[data-fg-product-preview-trigger][data-product-url]');
      const link = card.querySelector('a.product-card__link[href]');
      const rawUrl = trigger instanceof HTMLElement ? trigger.dataset.productUrl || '' : link?.href || '';
      const source = trigger instanceof HTMLElement ? trigger : link instanceof HTMLElement ? link : null;
      addProduct(rawUrl, source);
    }

    // Some editorial product links are not wrapped in a Horizon product-card.
    // Keep those in DOM order too, without duplicating cards already collected.
    for (const trigger of sequenceRoot.querySelectorAll(
      '[data-fg-product-preview-trigger][data-product-url]'
    )) {
      if (
        !(trigger instanceof HTMLElement) ||
        trigger.closest('#quick-add-dialog') ||
        trigger.closest('product-card')
      ) {
        continue;
      }
      addProduct(trigger.dataset.productUrl || '', trigger);
    }

    const currentKey = this.#getProductKey(currentProductUrl);
    if (currentKey && !seen.has(currentKey)) {
      products.unshift({ url: currentProductUrl, key: currentKey, trigger: this.#trigger });
    }

    return products;
  }

  /** @param {URL} productUrl */
  #getProductKey(productUrl) {
    const match = productUrl.pathname.match(/\/products\/([^/]+)/i);
    return match?.[1]?.toLowerCase() || '';
  }

  #updateProductNavigation() {
    const navigation = document.querySelector('[data-fg-product-preview-product-navigation]');
    if (!(navigation instanceof HTMLElement)) return;

    const products = this.#currentProductUrl ? this.#getProductSequence(this.#currentProductUrl) : [];
    navigation.hidden = products.length < 2 || !this.#currentProductUrl;

    for (const button of navigation.querySelectorAll('button')) {
      if (button instanceof HTMLButtonElement) {
        button.disabled = navigation.hidden || this.#productNavigationLoading;
        button.setAttribute('aria-busy', `${this.#productNavigationLoading}`);
      }
    }
  }

  /** @param {boolean} loading */
  #setProductNavigationLoading(loading) {
    this.#productNavigationLoading = loading;
    const navigation = document.querySelector('[data-fg-product-preview-product-navigation]');
    if (!(navigation instanceof HTMLElement)) return;

    for (const button of navigation.querySelectorAll('button')) {
      if (button instanceof HTMLButtonElement) {
        button.disabled = loading || navigation.hidden;
        button.setAttribute('aria-busy', `${loading}`);
      }
    }
  }

  /** @param {HTMLElement | null} trigger */
  #getPreviewSourceRect(trigger) {
    const source =
      trigger?.closest(
        'product-card, [data-product-card], .product-card, [data-fg-selector-card], .fg-focus__image-link'
      ) || trigger;

    if (!(source instanceof Element)) return null;

    const rect = source.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return null;

    return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @param {{left: number, top: number, width: number, height: number} | null} sourceRect
   */
  #setPreviewMotion(dialog, sourceRect) {
    const modalRect = this.#getPreviewTargetRect();
    let fromX = 0;
    let fromY = 12;
    let fromScale = 0.96;

    if (sourceRect) {
      const sourceCenterX = sourceRect.left + sourceRect.width / 2;
      const sourceCenterY = sourceRect.top + sourceRect.height / 2;
      const modalCenterX = modalRect.left + modalRect.width / 2;
      const modalCenterY = modalRect.top + modalRect.height / 2;

      fromX = sourceCenterX - modalCenterX;
      fromY = sourceCenterY - modalCenterY;
      fromScale = Math.min(
        0.82,
        Math.max(0.08, Math.min(sourceRect.width / modalRect.width, sourceRect.height / modalRect.height))
      );
    }

    dialog.style.setProperty('--fg-preview-from-x', `${fromX}px`);
    dialog.style.setProperty('--fg-preview-from-y', `${fromY}px`);
    dialog.style.setProperty('--fg-preview-from-scale', fromScale.toFixed(4));
  }

  #getPreviewTargetRect() {
    const isMobile = window.matchMedia(MOBILE_BREAKPOINT).matches;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (isMobile) {
      const height = Math.min(viewportHeight * 0.92, 52 * 16, viewportHeight - 8);
      return { left: 0, top: viewportHeight - height, width: viewportWidth, height };
    }

    const width = Math.min(90 * 16, viewportWidth - 3 * 16);
    const height = Math.min(50 * 16, viewportHeight - 3 * 16);
    return {
      left: (viewportWidth - width) / 2,
      top: (viewportHeight - height) / 2,
      width,
      height,
    };
  }

  /**
   * @param {HTMLElement | null} trigger
   * @param {HTMLElement} productDialog
   */
  async #closeSourceDialog(trigger, productDialog) {
    const sourceDialog = trigger?.closest('dialog[open]');
    const sourceComponent = sourceDialog?.closest('dialog-component, quick-add-dialog');

    if (
      sourceComponent instanceof HTMLElement &&
      sourceComponent !== productDialog &&
      typeof sourceComponent.closeDialog === 'function'
    ) {
      await sourceComponent.closeDialog();
    }
  }

  /** @param {string} productUrl */
  async #fetchProduct(productUrl) {
    const cachedProduct = this.#cache.get(productUrl);
    if (cachedProduct) return /** @type {Element} */ (cachedProduct.cloneNode(true));

    const controller = new AbortController();
    this.#activeFetch = controller;

    try {
      const response = await fetch(productUrl, {
        signal: controller.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) throw new Error(`Product request failed with status ${response.status}.`);

      const responseText = await response.text();
      const productDocument = new DOMParser().parseFromString(responseText, 'text/html');
      const productGrid = productDocument.querySelector('[data-product-grid-content]');

      if (!(productGrid instanceof Element)) throw new Error('Product content was missing from the response.');

      const cachedClone = /** @type {Element} */ (productGrid.cloneNode(true));
      this.#cache.set(productUrl, cachedClone);
      return /** @type {Element} */ (cachedClone.cloneNode(true));
    } finally {
      if (this.#activeFetch === controller) this.#activeFetch = null;
    }
  }

  /**
   * Rebuilds the compact product summary for the mobile dialog.
   * @param {Element} productGrid
   * @param {URL} productUrl
   */
  #prepareProductGrid(productGrid, productUrl) {
    if (!window.matchMedia(MOBILE_BREAKPOINT).matches) return;

    const productDetails = productGrid.querySelector('.product-details');
    const productForm = productGrid.querySelector('product-form-component');
    const variantPicker = productGrid.querySelector('variant-picker');
    const productPrice = productGrid.querySelector('product-price');
    const sourceTitle = productGrid.querySelector('.view-product-title a');
    const description = productGrid.querySelector('[data-fg-product-description]');

    const title = document.createElement('a');
    title.href = productUrl.toString();
    title.textContent = sourceTitle?.textContent?.trim() || window.fgText("Product");

    const productHeader = document.createElement('div');
    productHeader.className = 'product-header';
    productHeader.append(title);
    if (productPrice) productHeader.append(productPrice);

    productGrid.append(productHeader);
    if (variantPicker) productGrid.append(variantPicker);
    if (productForm) productGrid.append(productForm);
    if (description) productGrid.append(description);
    productDetails?.remove();
  }

  /** @param {HTMLElement} modalContent */
  #finishProductContent(modalContent) {
    this.#buildDesktopProductStage(modalContent);
    this.#prepareVariantMedia(modalContent);
    this.#mountFullProductPageLink(modalContent);
    this.#mountProductActions(modalContent);

    for (const titleLink of modalContent.querySelectorAll('.view-product-title a, .product-header > a')) {
      if (!(titleLink instanceof HTMLAnchorElement)) continue;
      titleLink.tabIndex = -1;
      titleLink.setAttribute('aria-disabled', 'true');
      titleLink.dataset.productPreviewTitle = 'true';
    }

    const firstHeading = modalContent.querySelector('.view-product-title a, .product-header > a');
    if (firstHeading instanceof HTMLElement) {
      firstHeading.setAttribute('role', 'heading');
      firstHeading.setAttribute('aria-level', '2');
    }
  }

  /** @param {HTMLElement} modalContent */
  #mountFullProductPageLink(modalContent) {
    const dialog = modalContent.closest('dialog');
    if (!(dialog instanceof HTMLDialogElement)) return;

    const rawUrl =
      this.#currentProductUrl?.toString() ||
      modalContent.dataset.fgProductUrl ||
      '';

    let productUrl;
    try {
      productUrl = new URL(rawUrl, window.location.href);
    } catch {
      return;
    }

    if (!PRODUCT_PATH_PATTERN.test(productUrl.pathname)) return;

    productUrl.searchParams.delete(PREVIEW_QUERY_PARAM);
    productUrl.searchParams.delete(REVIEW_QUERY_PARAM);
    productUrl.searchParams.delete('section_id');
    productUrl.hash = '';

    let link = dialog.querySelector('[data-fg-full-product-page]');
    if (!(link instanceof HTMLAnchorElement)) {
      link = document.createElement('a');
      link.className = 'fg-product-preview__full-page-link';
      link.dataset.fgFullProductPage = '';
      link.dataset.productPreview = 'off';
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.innerHTML = `
        <span>${window.fgText("SHOW FULL PRODUCT PAGE")}</span>
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
          <path d="M7 17 17 7M9 7h8v8"/>
        </svg>
      `;
      dialog.append(link);
    }

    link.href = productUrl.toString();
    link.setAttribute(
      'aria-label',
      `${window.fgText("SHOW FULL PRODUCT PAGE")}: ${modalContent.dataset.fgProductTitle || window.fgText("Product")}`
    );
  }

  /**
   * Copies Judge.me's product-specific widget root out of the fetched product
   * page. The storefront module can then initialize the same verified review
   * form inside the product preview without duplicating it on product pages.
   * @param {Document} productDocument
   * @param {Element} productGrid
   * @param {URL} productUrl
   */
  #attachJudgeMeWidget(productDocument, productGrid, productUrl) {
    const sourceWidget = productDocument.querySelector(JUDGE_ME_WIDGET_SELECTOR);
    if (!(sourceWidget instanceof HTMLElement)) return;

    const loaderScript = Array.from(productDocument.querySelectorAll('script[src]')).find(
      (script) => script.src.includes('judgeme') && script.src.includes('/loader.js')
    );
    const handle = this.#getProductKey(productUrl) || productGrid.getAttribute('data-fg-product-handle') || 'product';
    const host = productDocument.createElement('div');
    host.className = 'fg-product-preview__judge-host';
    host.dataset.fgJudgeMeHost = '';
    host.setAttribute('aria-hidden', 'true');
    if (loaderScript instanceof HTMLScriptElement) host.dataset.fgJudgeMeLoader = loaderScript.src;

    const widget = /** @type {HTMLElement} */ (sourceWidget.cloneNode(true));
    widget.id = `fg-judgeme-product-reviews-${handle}`;
    widget.classList.remove('jdgm--done-setup-widget', 'jdgm-widget-revamp');
    widget.removeAttribute('data-v-app');
    widget.removeAttribute('data-widget-name');
    widget.removeAttribute('data-impressions-tracked');
    widget.dataset.blockId = `${widget.dataset.blockId || 'judge-me'}-fg-${handle}`;
    host.append(widget);
    productGrid.append(host);
  }

  /**
   * Horizon intentionally preserves the quick-add target's own attributes
   * during morphing. Restore the product metadata required by review actions
   * and direct modal links after the product content has been mounted.
   * @param {HTMLElement} modalContent
   * @param {Element} productGrid
   */
  #applyProductMetadata(modalContent, productGrid) {
    modalContent.setAttribute('data-product-grid-content', '');

    for (const attribute of [
      'data-fg-product-id',
      'data-fg-product-handle',
      'data-fg-product-title',
      'data-fg-product-url',
    ]) {
      const value = productGrid.getAttribute(attribute);
      if (value !== null) modalContent.setAttribute(attribute, value);
    }
  }

  /** @param {HTMLElement} modalContent */
  #mountProductActions(modalContent) {
    if (modalContent.querySelector('[data-fg-product-actions]')) return;

    const actions = document.createElement('div');
    actions.className = 'fg-product-preview__actions';
    actions.dataset.fgProductActions = '';
    actions.innerHTML = `
      <button class="fg-product-preview__action" type="button" data-fg-write-review>
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
          <path d="m12 3 2.7 5.47 6.03.88-4.36 4.25 1.03 6-5.4-2.84-5.4 2.84 1.03-6-4.36-4.25 6.03-.88L12 3Z"/>
        </svg>
        <span>${window.fgText("WRITE A REVIEW")}</span>
      </button>
      <button class="fg-product-preview__action fg-product-preview__action--secondary" type="button" data-fg-copy-preview-link>
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
          <path d="M9.5 14.5 14.5 9M7.05 16.95l-1 1a3.5 3.5 0 0 1-4.95-4.95l4-4a3.5 3.5 0 0 1 4.95 0M16.95 7.05l1-1a3.5 3.5 0 0 1 4.95 4.95l-4 4a3.5 3.5 0 0 1-4.95 0"/>
        </svg>
        <span>${window.fgText("COPY LINK")}</span>
      </button>
      <span class="fg-product-preview__action-status" data-fg-product-action-status aria-live="polite"></span>
    `;

    const productForm = modalContent.querySelector('product-form-component');
    const detailsContent = modalContent.querySelector('.product-details .group-block-content');

    if (productForm instanceof HTMLElement) {
      productForm.after(actions);
    } else if (detailsContent instanceof HTMLElement) {
      detailsContent.append(actions);
    } else {
      modalContent.append(actions);
    }
  }

  /** @param {HTMLElement} modalContent */
  #initializeJudgeMe(modalContent) {
    const host = modalContent.querySelector('[data-fg-judge-me-host]');
    const button = modalContent.querySelector('[data-fg-write-review]');
    if (!(host instanceof HTMLElement) || !(button instanceof HTMLButtonElement)) {
      if (button instanceof HTMLButtonElement) {
        button.textContent = window.fgText("REVIEWS UNAVAILABLE");
        button.removeAttribute('aria-busy');
      }
      return;
    }

    const markReady = () => {
      const judgeButton = host.querySelector(JUDGE_ME_WRITE_SELECTOR);
      if (!(judgeButton instanceof HTMLElement)) return false;
      button.disabled = false;
      button.setAttribute('aria-busy', 'false');
      return true;
    };

    if (markReady()) return;

    let loaderUrl = host.dataset.fgJudgeMeLoader || '';

    if (!loaderUrl) {
      const loaderScript = Array.from(document.querySelectorAll('script[src]')).find(
        (script) => script.src.includes('judgeme') && script.src.includes('/loader.js')
      );
      if (loaderScript instanceof HTMLScriptElement) loaderUrl = loaderScript.src;
    }

    if (loaderUrl) {
      const script = document.createElement('script');
      const sourceUrl = new URL(loaderUrl, window.location.href);
      sourceUrl.searchParams.set('fg-product-preview', `${PRODUCT_PREVIEW_BUILD}-${++this.#judgeLoaderLoadId}`);
      script.src = sourceUrl.toString();
      script.dataset.fgJudgeMePreviewLoader = '';
      script.addEventListener('error', () => this.#setActionStatus(modalContent, 'REVIEW FORM UNAVAILABLE'));
      document.head.append(script);
    }

    let attempts = 0;
    const readinessTimer = window.setInterval(() => {
      attempts += 1;
      if (!modalContent.isConnected || markReady() || attempts >= 80) {
        window.clearInterval(readinessTimer);
        if (attempts >= 80 && button.disabled) {
          button.removeAttribute('aria-busy');
          this.#setActionStatus(modalContent, 'REVIEW FORM UNAVAILABLE');
        }
      }
    }, 100);
  }

  /** @param {HTMLButtonElement} button */
  #openJudgeMeReview(button) {
    const modalContent = button.closest('[data-product-grid-content]');
    if (!(modalContent instanceof HTMLElement)) return;
    this.#openReviewOnProductPage(modalContent);
  }

  /** @param {HTMLElement} modalContent */
  #openReviewOnProductPage(modalContent) {
    const productUrl = this.#currentProductUrl
      ? new URL(this.#currentProductUrl.toString())
      : new URL(modalContent.dataset.fgProductUrl || '', window.location.href);

    if (!PRODUCT_PATH_PATTERN.test(productUrl.pathname)) {
      this.#setActionStatus(modalContent, 'REVIEW FORM UNAVAILABLE');
      return;
    }

    productUrl.searchParams.set(REVIEW_QUERY_PARAM, '1');
    productUrl.hash = 'judgeme_product_reviews';
    window.location.assign(productUrl.toString());
  }

  /** @param {HTMLElement} modalContent */
  #moveJudgeModalIntoPreview(modalContent) {
    const dialog = modalContent.closest('dialog');
    if (!(dialog instanceof HTMLDialogElement)) return;

    const productTitle = modalContent.dataset.fgProductTitle?.trim().toLowerCase() || '';
    const modals = Array.from(document.querySelectorAll('.jdgm-write-review-modal'));
    const matchingModal = modals.find((modal) => {
      const title = modal.querySelector('.jdgm-write-review-modal__product-title')?.textContent?.trim().toLowerCase() || '';
      return productTitle && title.includes(productTitle);
    });
    const visibleModal = modals.find((modal) => getComputedStyle(modal).display !== 'none');
    const judgeModal = visibleModal || matchingModal || modals.at(-1);

    if (!(judgeModal instanceof HTMLElement)) return;
    judgeModal.dataset.fgPreviewJudgeModal = '';
    dialog.append(judgeModal);
  }

  #removePreviewJudgeModals() {
    for (const modal of document.querySelectorAll('[data-fg-preview-judge-modal]')) modal.remove();
    for (const script of document.querySelectorAll('[data-fg-judge-me-preview-loader]')) script.remove();
  }

  /** @param {HTMLButtonElement} button */
  async #copyPreviewLink(button) {
    if (!this.#currentProductUrl) return;

    const shareUrl = this.#getShareUrl(this.#currentProductUrl).toString();
    let copied = false;

    try {
      await navigator.clipboard.writeText(shareUrl);
      copied = true;
    } catch {
      const field = document.createElement('textarea');
      field.value = shareUrl;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      document.body.append(field);
      field.select();
      copied = document.execCommand('copy');
      field.remove();
    }

    const modalContent = button.closest('[data-product-grid-content]');
    this.#setActionStatus(modalContent, copied ? window.fgText("LINK COPIED") : window.fgText("COPY FAILED"));
  }

  /**
   * @param {Element | null | undefined} modalContent
   * @param {string} message
   */
  #setActionStatus(modalContent, message) {
    if (!(modalContent instanceof Element)) return;
    const status = modalContent.querySelector('[data-fg-product-action-status]');
    if (!(status instanceof HTMLElement)) return;

    status.textContent = message;
    window.clearTimeout(Number(status.dataset.clearTimer || 0));
    const timer = window.setTimeout(() => {
      if (status.textContent === message) status.textContent = '';
    }, 2600);
    status.dataset.clearTimer = `${timer}`;
  }

  /** @param {URL} productUrl */
  #syncPreviewUrl(productUrl) {
    if (!this.#urlBeforePreview) {
      const returnUrl = new URL(window.location.href);
      returnUrl.searchParams.delete(PREVIEW_QUERY_PARAM);
      this.#urlBeforePreview = returnUrl;
    }

    const previewUrl = new URL(window.location.href);
    previewUrl.searchParams.set(PREVIEW_QUERY_PARAM, this.#getProductKey(productUrl));
    const state = history.state && typeof history.state === 'object' ? { ...history.state } : {};
    state.fgProductPreview = this.#getProductKey(productUrl);
    history.replaceState(state, '', previewUrl);
  }

  #restorePreviewUrl() {
    if (!this.#urlBeforePreview) return;

    const state = history.state && typeof history.state === 'object' ? { ...history.state } : {};
    delete state.fgProductPreview;
    history.replaceState(state, '', this.#urlBeforePreview);
    this.#urlBeforePreview = null;
  }

  /** @param {URL} productUrl */
  #getShareUrl(productUrl) {
    const shareUrl = new URL(window.Shopify?.routes?.root || '/', window.location.origin);
    shareUrl.searchParams.set(PREVIEW_QUERY_PARAM, this.#getProductKey(productUrl));
    return shareUrl;
  }

  /**
   * Keeps Horizon's media and purchase columns in one explicit first panel.
   * The description stays as the following full-width section, so the base
   * quick-add grid cannot collapse the product area to a zero-height row.
   * @param {HTMLElement} modalContent
   */
  #buildDesktopProductStage(modalContent) {
    if (window.matchMedia(MOBILE_BREAKPOINT).matches) return;
    if (modalContent.querySelector(':scope > [data-fg-product-preview-stage]')) return;

    const media = modalContent.querySelector(':scope > .product-information__media');
    const details = modalContent.querySelector(':scope > .product-details');
    if (!(media instanceof HTMLElement) || !(details instanceof HTMLElement)) return;

    const stage = document.createElement('div');
    stage.className = 'fg-product-preview__stage';
    stage.dataset.fgProductPreviewStage = '';
    media.before(stage);
    stage.append(media, details);
  }

  /**
   * Keeps the selected variant's assigned mockup first while leaving every
   * remaining gallery item available for arrows, drag and mobile swipe.
   * @param {Element} modalContent
   */
  #prepareVariantMedia(modalContent) {
    const gallery = modalContent.querySelector('media-gallery');
    const slideshow = gallery?.querySelector('slideshow-component');

    if (!(gallery instanceof HTMLElement) || !(slideshow instanceof HTMLElement)) return;

    const slides = Array.from(
      slideshow.querySelectorAll(':scope > slideshow-container > slideshow-slides > slideshow-slide')
    );
    if (!slides.length) return;

    // Preserve each slide's canonical product-media position across refreshes.
    for (const [position, slide] of slides.entries()) {
      if (!slide.hasAttribute('data-fg-media-position')) {
        slide.setAttribute('data-fg-media-position', `${position}`);
      }
    }

    const selectedMediaPosition = this.#getSelectedVariantMediaPosition(modalContent);
    const selectedSlide =
      slides.find((slide) => this.#getSlideMediaPosition(slide) === selectedMediaPosition) || slides[0];
    const visibleSlides = [selectedSlide, ...slides.filter((slide) => slide !== selectedSlide)];
    const slideTrack = slideshow.querySelector('slideshow-slides');

    if (slideTrack) {
      for (const slide of visibleSlides) slideTrack.append(slide);
    }

    for (const slide of slides) {
      slide.removeAttribute('hidden');
      slide.removeAttribute('reveal');
      slide.setAttribute('aria-hidden', `${slide !== selectedSlide}`);
      slide.classList.add(VARIANT_MEDIA_GROUP_CLASS);
    }

    slideshow.setAttribute('initial-slide', '0');
    slideshow.toggleAttribute('infinite', visibleSlides.length > 1);
    slideshow.setAttribute('disabled', visibleSlides.length > 1 ? 'false' : 'true');
    slideshow.dataset.fgVariantMediaCount = `${visibleSlides.length}`;

    const controls = Array.from(slideshow.querySelectorAll('slideshow-controls'));
    const counter = slideshow.querySelector('.slideshow-controls__counter');
    if (counter instanceof HTMLElement) {
      const current = counter.querySelector('[ref="current"]');
      let total = counter.querySelector('[data-slideshow-total]');

      if (current instanceof HTMLElement) current.textContent = '1';

      if (!(total instanceof HTMLElement)) {
        total = document.createElement('span');
        total.dataset.slideshowTotal = '';
        counter.append(total);
      }

      total.textContent = `${visibleSlides.length}`;
    }

    if ('current' in slideshow) slideshow.current = 0;

    for (const control of controls) {
      const isCounterControl = Boolean(control.querySelector('.slideshow-controls__counter'));
      control.classList.remove('mobile:hidden', 'desktop:hidden');
      control.toggleAttribute('hidden', !isCounterControl || visibleSlides.length <= 1);
    }

    const arrows = slideshow.querySelector('slideshow-arrows');
    if (arrows instanceof HTMLElement) {
      arrows.classList.remove('mobile:hidden', 'desktop:hidden');
      arrows.toggleAttribute('hidden', visibleSlides.length <= 1);
    }

    gallery.dataset.fgVariantMediaReady = 'true';
  }

  /** @param {Element} modalContent */
  #getSelectedVariantMediaPosition(modalContent) {
    const selectedOption = modalContent.querySelector(
      'variant-picker input:checked[data-variant-media-position], variant-picker option:checked[data-variant-media-position]'
    );
    const position = Number.parseInt(selectedOption?.getAttribute('data-variant-media-position') || '', 10);
    return Number.isFinite(position) ? position : Number.NaN;
  }

  /** @param {Element} slide */
  #getSlideMediaPosition(slide) {
    const position = Number.parseInt(slide.getAttribute('data-fg-media-position') || '', 10);
    return Number.isFinite(position) ? position : Number.MAX_SAFE_INTEGER;
  }

  /**
   * @param {HTMLElement} dialogComponent
   * @param {HTMLElement} modalContent
   */
  #resetScroll(dialogComponent, modalContent) {
    modalContent.scrollTo({ top: 0, behavior: 'instant' });
    dialogComponent.querySelector('.product-details')?.scrollTo({ top: 0, behavior: 'instant' });
    dialogComponent.querySelector('slideshow-slides')?.scrollTo({ left: 0, behavior: 'instant' });
  }

  /** @param {HTMLElement} modalContent */
  #renderLoading(modalContent) {
    modalContent.setAttribute('aria-busy', 'true');
    modalContent.removeAttribute('view-event-payload');
    modalContent.innerHTML = `
      <div class="fg-product-preview__loading" role="status" aria-live="polite">
        <span class="fg-product-preview__eyebrow">${window.fgText("FG / PRODUCT FILE")}</span>
        <span class="fg-product-preview__spinner" aria-hidden="true"></span>
        <strong>${window.fgText("LOADING THE PIECE")}</strong>
      </div>
    `;
  }

  /**
   * @param {HTMLElement} modalContent
   * @param {URL} productUrl
   */
  #renderError(modalContent, productUrl) {
    modalContent.removeAttribute('aria-busy');
    modalContent.innerHTML = `
      <div class="fg-product-preview__error" role="alert">
        <span class="fg-product-preview__eyebrow">${window.fgText("FG / CONNECTION INTERRUPTED")}</span>
        <strong>${window.fgText("THE PRODUCT COULD NOT BE LOADED.")}</strong>
        <p>${window.fgText("Try again, or open the full product page.")}</p>
        <div class="fg-product-preview__error-actions">
          <button type="button" data-fg-product-preview-retry>${window.fgText("TRY AGAIN")}</button>
          <a data-product-preview="off">${window.fgText("OPEN PRODUCT")}</a>
        </div>
      </div>
    `;

    const retryButton = modalContent.querySelector('[data-fg-product-preview-retry]');
    const fallbackLink = modalContent.querySelector('a[data-product-preview="off"]');
    if (retryButton instanceof HTMLButtonElement) retryButton.dataset.productUrl = productUrl.toString();
    if (fallbackLink instanceof HTMLAnchorElement) fallbackLink.href = productUrl.toString();
  }
}

new FGProductPreview();
